/**
 * Shared admin sidebar / mobile menu configuration.
 */

import {
  PANEL_PERMISSIONS,
  type PanelPermission,
  hasPanelPermission,
} from '../../utils/panelPermissions';

export interface AdminNavChild {
  icon: string;
  text: string;
  path: string;
  permission?: PanelPermission;
  /** @deprecated Prefer `permission` for fine-grained access. */
  adminOnly?: boolean;
}

export interface AdminNavItemBase {
  icon: string;
  text: string;
  /** @deprecated Prefer `permission` for fine-grained access. */
  adminOnly?: boolean;
  permission?: PanelPermission;
}

export interface AdminNavLinkItem extends AdminNavItemBase {
  path: string;
}

export interface AdminNavGroupItem extends AdminNavItemBase {
  path: null;
  groupKey: string;
  children: AdminNavChild[];
}

export type AdminNavItem = AdminNavLinkItem | AdminNavGroupItem;

export const isNavGroup = (item: AdminNavItem): item is AdminNavGroupItem =>
  item.path === null && 'children' in item;

export const getAdminNavItems = (
  isAdmin: boolean,
  permissions?: string[] | null,
  isGodMode = false
): AdminNavItem[] => {
  const can = (permission?: PanelPermission, adminOnly?: boolean) => {
    if (isGodMode) return true;
    if (permission) {
      // null/undefined ⇒ not loaded yet; fall back to legacy isAdmin.
      if (permissions == null) {
        return adminOnly ? isAdmin : true;
      }
      return hasPanelPermission(permissions, permission, isGodMode);
    }
    if (adminOnly) return isAdmin;
    return true;
  };

  const items: AdminNavItem[] = [
    { icon: 'home', text: 'Inicio', path: '/home' },
    {
      icon: 'orders-group',
      text: 'Órdenes',
      path: null,
      groupKey: 'orders',
      permission: PANEL_PERMISSIONS.ORDERS,
      children: [
        {
          icon: 'orders',
          text: 'Ventas',
          path: '/orders',
          permission: PANEL_PERMISSIONS.ORDERS,
        },
        {
          icon: 'quotes',
          text: 'Cotizaciones',
          path: '/quotations',
          permission: PANEL_PERMISSIONS.QUOTATIONS,
        },
        {
          icon: 'clients-orders',
          text: 'Pedidos de clientes',
          path: '/client-orders',
          permission: PANEL_PERMISSIONS.ORDERS,
        },
        {
          icon: 'returns',
          text: 'Devoluciones',
          path: '/returns',
          permission: PANEL_PERMISSIONS.ORDERS,
        },
      ],
    },
    {
      icon: 'inventory',
      text: 'Inventario',
      path: '/inventory',
      adminOnly: true,
      permission: PANEL_PERMISSIONS.INVENTORY,
    },
    {
      icon: 'finance-group',
      text: 'Finanzas',
      path: null,
      groupKey: 'finance',
      children: [
        {
          icon: 'accounts-receivable',
          text: 'Cuentas por Cobrar',
          path: '/accounts-receivable',
          permission: PANEL_PERMISSIONS.ACCOUNTS_RECEIVABLE,
        },
        {
          icon: 'accounts-payable',
          text: 'Cuentas por Pagar',
          path: '/accounts-payable',
          adminOnly: true,
          permission: PANEL_PERMISSIONS.ACCOUNTS_PAYABLE,
        },
      ],
    },
    {
      icon: 'purchasing-group',
      text: 'Compras',
      path: null,
      groupKey: 'purchasing',
      adminOnly: true,
      permission: PANEL_PERMISSIONS.PURCHASING,
      children: [
        {
          icon: 'suppliers',
          text: 'Proveedores',
          path: '/suppliers',
          permission: PANEL_PERMISSIONS.PURCHASING,
        },
        {
          icon: 'purchases',
          text: 'Compras',
          path: '/purchases',
          permission: PANEL_PERMISSIONS.PURCHASING,
        },
      ],
    },
    {
      icon: 'clients',
      text: 'Clientes',
      path: '/clients',
      permission: PANEL_PERMISSIONS.CLIENTS,
    },
    {
      icon: 'vendors',
      text: 'Vendedores',
      path: '/vendors',
      permission: PANEL_PERMISSIONS.VENDORS,
    },
    {
      icon: 'configuration',
      text: 'Configuración',
      path: '/configuration',
      adminOnly: true,
      // Shown if configuration OR manage_users (checked below)
      permission: PANEL_PERMISSIONS.CONFIGURATION,
    },
  ];

  return items
    .map(item => {
      if (isNavGroup(item)) {
        // Group visible if any child is visible (or group permission)
        const children = item.children.filter(child =>
          can(child.permission, child.adminOnly ?? item.adminOnly)
        );
        if (children.length === 0) return null;
        return { ...item, children };
      }
      if (item.path === '/configuration') {
        if (isGodMode) return item;
        if (permissions == null) {
          return item.adminOnly ? (isAdmin ? item : null) : item;
        }
        const canConfig =
          hasPanelPermission(
            permissions,
            PANEL_PERMISSIONS.CONFIGURATION,
            isGodMode
          ) ||
          hasPanelPermission(
            permissions,
            PANEL_PERMISSIONS.MANAGE_USERS,
            isGodMode
          );
        return canConfig ? item : null;
      }
      if (!can(item.permission, item.adminOnly)) {
        return null;
      }
      return item;
    })
    .filter(Boolean) as AdminNavItem[];
};

/** Path prefixes that belong to a collapsible nav group */
export const NAV_GROUP_PATH_PREFIXES: Record<string, string[]> = {
  orders: ['/orders', '/quotations', '/client-orders', '/returns'],
  finance: ['/accounts-receivable', '/accounts-payable'],
  purchasing: ['/suppliers', '/purchases', '/purchases-suppliers'],
};
